import React, { useEffect, useState } from 'react';
import { deliveryAPI, DeliveryPage, RideAlong, RideAlongCadence, ScannedProduct } from '../../services/deliveryAPI';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import ZoomImage from './feed/ZoomImage';

/**
 * "Also bring": something we deliver WITH the feed that lives in no bin - wild bird seed,
 * litter, a chew (Brad, 2026-09-22: "Its hard to do wild bird bins"). No animals, no
 * consumption, no bin: pick it from the catalog, say how many and how often, done. It rides
 * along with the next feed delivery and never makes a trip on its own.
 *
 * Two steps in one dialog. PICK: species chips over a search box; wild bird browses without
 * typing because a customer knows the aisle, not the name. THEN: how many and how often.
 * Opened with `existing` it skips the pick and only changes count and cadence.
 */

const RED = '#8B0000';
const SPECIES: { key: string; label: string }[] = [
  { key: 'wild_bird', label: 'Wild bird' }, { key: 'dog', label: 'Dog' }, { key: 'cat', label: 'Cat' },
  { key: 'poultry', label: 'Poultry' }, { key: '', label: 'Everything' },
];
export const CADENCES: { key: RideAlongCadence; label: string }[] = [
  { key: 'next', label: 'Next delivery only' }, { key: 'every', label: 'Every delivery' },
  { key: 'every_2', label: 'Every 2nd delivery' }, { key: 'every_3', label: 'Every 3rd delivery' },
];
const money = (v: string | null | undefined) => (v == null ? '' : `$${Number(v).toFixed(2)}`);
const muted: React.CSSProperties = { color: '#6b7280', fontSize: 13 };
const chip = (on: boolean): React.CSSProperties => ({
  minHeight: 36, padding: '0 12px', borderRadius: 18, border: `1px solid ${on ? RED : '#d1d5db'}`,
  background: on ? RED : '#fff', color: on ? '#fff' : '#111', fontWeight: 600, fontSize: 14, cursor: 'pointer',
});
const stepper: React.CSSProperties = { width: 44, height: 44, borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontSize: 22, fontWeight: 700, cursor: 'pointer', lineHeight: 1 };

interface Props {
  existing?: RideAlong | null;
  onClose: () => void;
  onSaved: (page: DeliveryPage) => void;
}

const RideAlongDialog: React.FC<Props> = ({ existing, onClose, onSaved }) => {
  const [species, setSpecies] = useState('wild_bird');
  const { value: query, setValue: setQuery, debounced } = useDebouncedSearch('', 300);
  const [hits, setHits] = useState<ScannedProduct[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [picked, setPicked] = useState<ScannedProduct | null>(null);
  const [bagCount, setBagCount] = useState(existing?.bag_count ?? 1);
  const [cadence, setCadence] = useState<RideAlongCadence>(existing?.cadence ?? 'every');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "More about this feed", per row: the distributor's ingredients and attributes when
  // the asset import pulled them. Seed Factory rows have none yet.
  const [openInfo, setOpenInfo] = useState<Set<number>>(new Set());
  const toggleInfo = (id: number) => setOpenInfo((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const choosing = !existing && picked === null;

  useEffect(() => {
    if (!choosing) return undefined;
    let alive = true;
    // Browsing a species needs no text; "Everything" needs two characters or the list
    // would be the whole catalog.
    if (!species && debounced.trim().length < 2) { setHits([]); setTotal(null); return undefined; }
    setSearching(true);
    deliveryAPI.searchCatalog(debounced.trim(), species || undefined, { rideAlong: true })
      .then((res) => {
        if (!alive) return;
        setHits(res.results); setTotal(res.truncated ? (res.total ?? null) : null); setSearchError(null);
      })
      .catch(() => { if (alive) { setHits([]); setTotal(null); setSearchError("The catalog isn't answering right now. Try again in a moment."); } })
      .finally(() => { if (alive) setSearching(false); });
    return () => { alive = false; };
  }, [choosing, species, debounced]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const page = existing
        ? await deliveryAPI.setRideAlong(existing.id, { bag_count: bagCount, cadence })
        : await deliveryAPI.addRideAlong({ product_id: picked!.id, bag_count: bagCount, cadence });
      onSaved(page);
    } catch (err: any) {
      setError(err?.error || err?.detail || "That didn't save. Please try again.");
    } finally { setSaving(false); }
  };

  const name = existing
    ? (existing.product.full_name || `${existing.product.brand} ${existing.product.display_name}`)
    : picked ? (picked.full_name || `${picked.brand} ${picked.display_name}`) : '';

  return (
    <div
      className="modal-overlay"
      onClick={() => { if (!saving) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 16px 100px' }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: 'calc(100vh - 140px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '14px 16px 8px', borderBottom: '1px solid #eee', flex: '0 0 auto' }}>
          <h3 style={{ margin: 0, fontSize: 18, color: RED }}>{existing ? 'Change a ride-along' : 'Also bring…'}</h3>
          <p style={{ ...muted, margin: '4px 0 0' }}>
            {choosing ? 'Comes with your feed delivery. No bin needed.' : name}
          </p>
        </div>

        {choosing ? (
          <div style={{ padding: '8px 16px', overflow: 'hidden', flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: '0 0 auto', marginBottom: 8 }}>
              {SPECIES.map((s) => (
                <button key={s.key || 'all'} type="button" style={chip(species === s.key)} onClick={() => setSpecies(s.key)}>{s.label}</button>
              ))}
            </div>
            <input
              type="text"
              className="product-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={species ? 'Narrow the list…' : 'Search the whole catalog…'}
              // Enter closes the keyboard so the list is not hidden behind it.
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
              enterKeyHint="search"
              style={{ flex: '0 0 auto', minHeight: 44, fontSize: 16 }}
            />
            <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 220, marginTop: 8 }} data-testid="ride-along-list">
              {searchError && <p style={{ ...muted, color: '#b91c1c', fontWeight: 600 }}>{searchError}</p>}
              {!searchError && !searching && hits.length === 0 && (
                <p style={muted}>{!species && debounced.trim().length < 2 ? 'Type a brand or product name.' : `Nothing matched${debounced ? ` "${debounced}"` : ''}.`}</p>
              )}
              {total !== null && <p style={muted}>Showing {hits.length} of {total}. Add a word to narrow the list.</p>}
              {hits.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="product-option"
                  onClick={() => setPicked(p)}
                  style={{ display: 'grid', gridTemplateColumns: p.image_url ? '56px 1fr' : '1fr', columnGap: 12, alignItems: 'start', width: '100%', textAlign: 'left', padding: '12px 12px' }}
                >
                  {p.image_url && <ZoomImage src={p.image_url} size={56} />}
                  <span style={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.3, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: 16 }}>{p.full_name || `${p.brand} — ${p.display_name}`}</span>
                    {p.full_name && <><br /><span style={{ fontSize: 13, color: '#888' }}>{p.brand} — {p.display_name}</span></>}
                    <br />
                    <span style={{ ...muted, display: 'inline-block', marginTop: 6, fontSize: 14 }}>
                      {p.weight_lbs ? `${Number(p.weight_lbs)} lb` : ''}{p.unit_retail ? ` · ${money(p.unit_retail)}` : ''}
                    </span>
                    {(p.ingredients || (p.attributes && Object.keys(p.attributes).length > 0)) && (
                      <>
                        {' '}
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); toggleInfo(p.id); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); toggleInfo(p.id); } }}
                          style={{ display: 'inline-block', marginTop: 6, marginLeft: 8, fontSize: 14, color: RED, fontWeight: 600, textDecoration: 'underline', minHeight: 32 }}
                        >
                          {openInfo.has(p.id) ? 'Less' : 'More about this feed'}
                        </span>
                        {openInfo.has(p.id) && (
                          <span style={{ display: 'block', marginTop: 8, fontSize: 15, color: '#374151', lineHeight: 1.5, fontWeight: 400 }}>
                            {p.attributes && Object.entries(p.attributes).filter(([k]) => k !== 'Brand').map(([k, v]) => (
                              <span key={k} style={{ display: 'block' }}><strong>{k.replace(/_/g, ' ')}:</strong> {(v || []).join(', ')}</span>
                            ))}
                            {p.ingredients && <span style={{ display: 'block', marginTop: 4 }}><strong>Ingredients:</strong> {p.ingredients}</span>}
                            <span style={{ display: 'block', marginTop: 4, color: '#888' }}>From the distributor's product listing.</span>
                          </span>
                        )}
                      </>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ padding: '12px 16px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>How many bags?</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <button type="button" style={stepper} aria-label="One fewer" disabled={bagCount <= 1} onClick={() => setBagCount((n) => Math.max(1, n - 1))}>−</button>
              <span style={{ minWidth: 26, textAlign: 'center', fontWeight: 700, fontSize: 18 }}>{bagCount}</span>
              <button type="button" style={stepper} aria-label="One more" disabled={bagCount >= 20} onClick={() => setBagCount((n) => Math.min(20, n + 1))}>+</button>
            </div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>How often?</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {CADENCES.map((c) => (
                <button key={c.key} type="button" onClick={() => setCadence(c.key)}
                  style={{ minHeight: 44, textAlign: 'left', padding: '0 12px', borderRadius: 8, border: `2px solid ${cadence === c.key ? RED : '#e5e7eb'}`, background: cadence === c.key ? '#fdf2f2' : '#fff', fontWeight: cadence === c.key ? 700 : 500, cursor: 'pointer' }}>
                  {c.label}
                </button>
              ))}
            </div>
            <p style={{ ...muted, marginTop: 12 }}>It rides along with your feed. We never make a trip for it alone.</p>
          </div>
        )}

        <div style={{ padding: '10px 16px 14px', borderTop: '1px solid #eee', flex: '0 0 auto' }}>
          {error && <p style={{ ...muted, color: '#b91c1c', fontWeight: 600, margin: '0 0 8px' }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-secondary" style={{ flex: 1, minHeight: 44 }} disabled={saving}
              onClick={() => (picked && !existing ? setPicked(null) : onClose())}>{picked && !existing ? '‹ Back' : 'Cancel'}</button>
            <button type="button" className="btn-primary" style={{ flex: 1, minHeight: 44 }} disabled={saving || choosing} onClick={save}>
              {saving ? 'Saving…' : existing ? 'Save' : 'Bring it'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideAlongDialog;
